/* Assign staff: the jobs your agency won, and who you send.
 *
 * How a job is staffed depends on its shape (binding/shapes.go):
 *   - nurse, ICU at home, physiotherapy, doctor: one person per SHIFT (or
 *     visit), role NURSE_k — k up to the number of people the family asked for;
 *   - ambulance: ONE bundle for the whole trip — exactly one ambulance and one
 *     driver, a paramedic for ALS/ICU, attendants optional;
 *   - equipment: one unit per unit asked for.
 * The server re-checks everything (papers, qualification, leave, overlap) at
 * bind time and says why it refuses; the candidate list shows the same
 * refusals ahead of time. A nurse who is sent must still accept in their app.
 *
 * Built like the console's Bookings screen: tiles that filter, one table, and
 * a wide drawer holding the job, its shifts and the people on it. The route
 * /assign/:itemId opens the drawer directly (Requests sends you there after an
 * accept, Live jobs on a row click). */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  bindItem,
  checkOutOnBehalf,
  getItem,
  giveBackJob,
  listCandidates,
  listItems,
  withdrawAssignment,
  type Assignment,
  type BindAssignment,
  type Candidate,
  type ItemDetail,
  type ItemSummary,
  type Shift,
} from '../data/agency';
import { dateOnly, dateTime } from '../data/format';
import { assignmentStatus, errorText, itemStatus, jobName, refusalText, roleName, staffType, unmetText } from '../data/words';
import { canDispatch } from '../nav';
import { useProfile } from '../shell/profile';
import {
  ActionWithReason,
  AllClear,
  Btn,
  Card,
  CardHead,
  CellStack,
  Drawer,
  DrawerBody,
  DrawerHead,
  Empty,
  Field,
  Kpi,
  KpiGrid,
  Note,
  Stack,
  Table,
  Tag,
  TRow,
  uiCss,
} from '../ui';
import { Facts, ServiceDot, ServiceFacts, useAsync, useNotice } from './common';

const LIVE = new Set(['PROPOSED', 'ACCEPTED', 'ACTIVE']);
const SHIFT_SERVICES = new Set(['NURSE', 'HOME_ICU', 'PHYSIO', 'DOCTOR']);
const COLS = '1.6fr 1.1fr 1.2fr 1.4fr 1fr';

type Focus = 'all' | 'unstaffed' | 'gap' | 'covered' | 'checkout';

const ITEM_TONE: Record<string, { fg: string; bg: string }> = {
  AGENCY_ACCEPTED: { fg: 'var(--amber)', bg: 'var(--amber-bg)' },
  ASSIGNED: { fg: 'var(--teal)', bg: 'var(--teal-bg)' },
  IN_PROGRESS: { fg: 'var(--ok)', bg: 'var(--ok-bg)' },
};

export function ItemStatusTag({ status }: { status: string }) {
  const t = ITEM_TONE[status] ?? { fg: 'var(--ink-soft)', bg: 'var(--surface-field)' };
  return (
    <Tag fg={t.fg} bg={t.bg}>
      {itemStatus(status)}
    </Tag>
  );
}

function datesOf(r: { start_date: string | null; end_date: string | null }): string {
  if (!r.start_date) return '—';
  return dateOnly(r.start_date) + (r.end_date && r.end_date !== r.start_date ? ` – ${dateOnly(r.end_date)}` : '');
}

const isUnstaffed = (r: ItemSummary) => r.status === 'AGENCY_ACCEPTED';
const hasGap = (r: ItemSummary) => r.status !== 'AGENCY_ACCEPTED' && r.coverage.next_uncovered_shift_at !== null;
const isCovered = (r: ItemSummary) => r.status !== 'AGENCY_ACCEPTED' && r.coverage.next_uncovered_shift_at === null;
// 000097: someone on it is still checked in well after their shift ended.
const checkoutOverdue = (r: ItemSummary) => (r.checkout_overdue ?? 0) > 0;

export function AssignScreen() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const items = useAsync(listItems, [], 30_000);
  const [focus, setFocus] = useState<Focus>('all');
  const rows = useMemo(() => items.data ?? [], [items.data]);

  const counts = {
    all: rows.length,
    unstaffed: rows.filter(isUnstaffed).length,
    gap: rows.filter(hasGap).length,
    covered: rows.filter(isCovered).length,
    checkout: rows.filter(checkoutOverdue).length,
  };
  const shown = rows.filter((r) =>
    focus === 'unstaffed'
      ? isUnstaffed(r)
      : focus === 'gap'
        ? hasGap(r)
        : focus === 'covered'
          ? isCovered(r)
          : focus === 'checkout'
            ? checkoutOverdue(r)
            : true,
  );
  const pick = (f: Focus) => setFocus((cur) => (cur === f && f !== 'all' ? 'all' : f));
  const tone = (f: Focus) => (focus === f ? { bg: 'var(--teal-bg)', bd: 'var(--teal)', c: 'var(--teal)' } : undefined);

  return (
    <Stack>
      <KpiGrid>
        <Kpi label="Open jobs" value={counts.all} note="every job you won and is not finished" onClick={() => pick('all')} tone={tone('all')} />
        <Kpi
          label="To assign"
          value={counts.unstaffed}
          note="won, nobody on them yet"
          color={counts.unstaffed ? 'var(--amber)' : undefined}
          onClick={() => pick('unstaffed')}
          tone={tone('unstaffed')}
        />
        <Kpi
          label="Partly covered"
          value={counts.gap}
          note="a shift ahead has nobody"
          color={counts.gap ? 'var(--amber)' : undefined}
          onClick={() => pick('gap')}
          tone={tone('gap')}
        />
        <Kpi label="Fully assigned" value={counts.covered} note="everyone is in place" onClick={() => pick('covered')} tone={tone('covered')} />
        <Kpi
          label="Check-out overdue"
          value={counts.checkout}
          note="shift ended, nurse still checked in"
          color={counts.checkout ? 'var(--danger)' : undefined}
          onClick={() => pick('checkout')}
          tone={tone('checkout')}
        />
      </KpiGrid>
      {counts.checkout ? (
        <Note tone="danger">
          {counts.checkout === 1 ? 'One job has' : `${counts.checkout} jobs have`} a nurse still checked in after the shift ended. Call
          her, ask when she left, then open the job and check her out on her behalf — the job cannot finish until then.
        </Note>
      ) : null}

      <Card>
        <CardHead
          title="Jobs you won"
          sub="Choose who goes. Staff confirm in the Apkicare Partner app; the list re-reads every 30 seconds."
        />
        {items.error ? (
          <div style={{ padding: '0 18px 18px' }}>
            <Note tone="danger">Your jobs could not be read: {items.error}</Note>
          </div>
        ) : items.loading && rows.length === 0 ? (
          <Empty>Reading your jobs…</Empty>
        ) : shown.length === 0 ? (
          rows.length === 0 ? (
            <AllClear title="No open jobs" sub="Requests you accept appear here for you to staff." />
          ) : (
            <Empty>Nothing matches this filter. Click the tile again to see every job.</Empty>
          )
        ) : (
          <Table cols={COLS} head={['Job', 'Area', 'When', 'Coverage', 'Status']} minWidth={820}>
            {shown.map((r) => (
              <TRow key={r.public_id} cols={COLS} onClick={() => navigate(`/assign/${encodeURIComponent(r.public_id)}`)} selected={r.public_id === itemId}>
                <CellStack
                  top={
                    <span style={{ font: '700 13.5px var(--font-ui)', color: 'var(--ink)' }}>
                      <ServiceDot code={r.service_code} />
                      {jobName(r.service_code, r.variant_code)}
                    </span>
                  }
                  sub={<span className={uiCss.mono}>{r.public_id}</span>}
                />
                <span className={uiCss.sub}>{[r.locality.pincode, r.locality.city_code].filter(Boolean).join(' · ') || '—'}</span>
                <span>{datesOf(r)}</span>
                <CellStack
                  top={r.coverage.assigned_until ? `Covered until ${dateTime(r.coverage.assigned_until)}` : 'Nobody yet'}
                  sub={r.coverage.next_uncovered_shift_at ? `Gap from ${dateTime(r.coverage.next_uncovered_shift_at)}` : undefined}
                />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                  <ItemStatusTag status={r.status} />
                  {checkoutOverdue(r) ? (
                    <Tag fg="var(--danger)" bg="var(--danger-bg)">
                      Check-out overdue
                    </Tag>
                  ) : null}
                </span>
              </TRow>
            ))}
          </Table>
        )}
      </Card>

      <Drawer open={!!itemId} onClose={() => navigate('/assign')} label="Job" wide>
        {itemId ? <ItemDrawer itemId={itemId} onClose={() => navigate('/assign')} onChanged={items.reload} /> : null}
      </Drawer>
    </Stack>
  );
}

/* ------------------------------------------------------------------ one job */

interface Slot {
  role: string;
  shift?: Shift;
  label: string;
}

function ItemDrawer({ itemId, onClose, onChanged }: { itemId: string; onClose: () => void; onChanged: () => void }) {
  const { profile, reload: reloadProfile } = useProfile();
  const mayAct = canDispatch(profile.me.role);
  const item = useAsync(() => getItem(itemId), [itemId]);
  const { notice, setNotice, run } = useNotice();
  const [picking, setPicking] = useState<{ slots: Slot[]; title: string } | null>(null);
  const [withdrawing, setWithdrawing] = useState<Assignment | null>(null);
  const [checkingOut, setCheckingOut] = useState<Assignment | null>(null);
  // 2026-10-30: a job this agency cannot do goes back to everyone else at once.
  const [givingBack, setGivingBack] = useState(false);
  const [leftAt, setLeftAt] = useState('');

  const refresh = () => {
    item.reload();
    onChanged();
    void reloadProfile();
  };

  const d = item.data;
  // 000097: people still checked in after their shift ended. Nothing closes a
  // shift by itself; the agency checks them out, with the time they left.
  const stillIn = d ? d.assignments.filter((a) => stillCheckedIn(a, d.shifts)) : [];
  const openCheckOut = (a: Assignment) => {
    const end = d?.shifts.find((s) => s.public_id === a.shift_public_id)?.end_utc;
    setLeftAt(toLocalInput(end ? new Date(Math.min(new Date(end).getTime(), Date.now())) : new Date()));
    setCheckingOut(a);
  };
  return (
    <>
      <DrawerHead onClose={onClose}>
        <div className={uiCss.label}>Job · {itemId}</div>
        <div style={{ font: '700 19px/1.25 var(--font-display)', marginTop: 4 }}>{d ? jobName(d.service_code, d.variant_code) : 'Reading the job…'}</div>
        {d ? (
          <div className={uiCss.cardSub} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <ItemStatusTag status={d.status} />
            <span>Booking {d.booking_public_id}</span>
            {mayAct && (d.status === 'AGENCY_ACCEPTED' || d.status === 'ASSIGNED') ? (
              <Btn size="xs" variant="danger" onClick={() => setGivingBack(true)}>
                Give this job back
              </Btn>
            ) : null}
          </div>
        ) : null}
      </DrawerHead>
      <DrawerBody>
        <Stack>
          {notice}
          {item.error ? <Note tone="danger">The job could not be read: {item.error}</Note> : null}
          {!d && !item.error ? <Empty>Reading the job…</Empty> : null}
          {stillIn.map((a) => (
            <Note key={a.public_id} tone="danger">
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ flex: 1, minWidth: 240 }}>
                  <strong>{a.resource_label}</strong> is still checked in — the shift ended
                  {(() => {
                    const end = d?.shifts.find((s) => s.public_id === a.shift_public_id)?.end_utc;
                    return end ? ` ${dateTime(end)}` : '';
                  })()}
                  . Call them, ask when they left, and check them out: the job cannot finish until then.
                </span>
                {mayAct ? (
                  <Btn size="sm" variant="danger" onClick={() => openCheckOut(a)}>
                    Check out for them
                  </Btn>
                ) : null}
              </div>
            </Note>
          ))}
          {d ? (
            <>
              <Card flat>
                <CardHead title="The visit" sub="Opening a job is recorded on the platform as a view of the patient's details." />
                <div style={{ padding: '0 16px 16px', display: 'grid', gap: 12 }}>
                  <Facts
                    wide
                    items={[
                      [
                        'Patient',
                        [d.patient.first_name, d.patient.approximate_age !== null ? `${d.patient.approximate_age} yrs` : '', d.patient.sex?.toLowerCase()]
                          .filter(Boolean)
                          .join(', ') || '—',
                      ],
                      [
                        'Address',
                        [d.service_address.text, d.service_address.landmark ? `near ${d.service_address.landmark}` : '', d.service_address.pincode]
                          .filter(Boolean)
                          .join(', ') || '—',
                      ],
                      [
                        'Contact at the door',
                        d.contact.phone ? (
                          <a href={`tel:${d.contact.phone}`} style={{ color: 'inherit' }}>
                            {[d.contact.name, d.contact.phone].filter(Boolean).join(' · ')}
                          </a>
                        ) : (
                          d.contact.name || '—'
                        ),
                      ],
                      ['Covered until', dateTime(d.coverage.assigned_until)],
                      ['Next shift with nobody', dateTime(d.coverage.next_uncovered_shift_at)],
                      ...(d.service_code === 'NURSE' || d.nurse_preference
                        ? [['Family asked for', d.nurse_preference ? d.nurse_preference.label : 'Anyone suitable'] as [string, string]]
                        : []),
                    ]}
                  />
                  <ServiceFacts source={d.detail} flat />
                </div>
              </Card>
              {!mayAct ? <Note tone="amber">Your role can read jobs but not staff them. An owner, admin or dispatcher chooses who goes.</Note> : null}
              <Staffing item={d} mayAct={mayAct} onPick={setPicking} onWithdraw={setWithdrawing} />
              <Card flat>
                <CardHead title="Everyone on this job" sub="Who was sent, what they answered, and when they checked in and out." />
                <AssignmentTable
                  assignments={d.assignments}
                  shifts={d.shifts}
                  serviceCode={d.service_code}
                  mayAct={mayAct}
                  onWithdraw={setWithdrawing}
                  onCheckOut={openCheckOut}
                />
              </Card>
            </>
          ) : null}
        </Stack>
      </DrawerBody>

      {picking && d ? (
        <PickDialog
          item={d}
          slots={picking.slots}
          title={picking.title}
          onClose={() => setPicking(null)}
          onBound={(msg) => {
            setPicking(null);
            setNotice({ tone: 'ok', text: msg });
            refresh();
          }}
        />
      ) : null}

      <ActionWithReason
        open={withdrawing !== null}
        onClose={() => setWithdrawing(null)}
        kicker="Take someone off a job"
        title={withdrawing ? `Take ${withdrawing.resource_label} off this job` : ''}
        desc={withdrawing ? `${roleName(withdrawing.role, d?.service_code)} · ${assignmentStatus(withdrawing.status)}` : ''}
        codes={[]}
        impact="They are told in their app, and the place is open again for you to fill."
        okLabel="Take off"
        okTone="danger"
        onConfirm={() => {
          const a = withdrawing;
          setWithdrawing(null);
          if (!a) return;
          void run(() => withdrawAssignment(a.public_id), `${a.resource_label} was taken off this job.`).then(refresh);
        }}
      />

      <ActionWithReason
        open={givingBack}
        onClose={() => setGivingBack(false)}
        kicker="Give this job back"
        title={d ? `Give back this ${jobName(d.service_code, d.variant_code).toLowerCase()} job` : ''}
        desc="If you cannot do it — no ambulance or driver free, nobody for these dates — give it back now rather than keep the family waiting."
        codes={GIVE_BACK_REASONS}
        impact="Anyone you put on it is taken off. It is offered again at once to the other agencies and independent workers near the family, and not to you."
        okLabel="Give it back"
        okTone="danger"
        onConfirm={({ code, note }) => {
          setGivingBack(false);
          const label = GIVE_BACK_REASONS.find((c) => c.code === code)?.label ?? code;
          const reason = note.trim() ? `${label}: ${note.trim()}` : label;
          void run(() => giveBackJob(itemId, reason), 'Given back. It is being offered again to the others near the family.').then((ok) => {
            if (ok) {
              onChanged();
              void reloadProfile();
              onClose();
            }
          });
        }}
      />

      <ActionWithReason
        open={checkingOut !== null}
        onClose={() => setCheckingOut(null)}
        kicker="Check out on their behalf"
        title={checkingOut ? `Check ${checkingOut.resource_label} out` : ''}
        desc="For someone who forgot to check out, or whose phone died. Use the time they actually left — ask them. It is recorded as done by your agency, with your reason."
        codes={CHECKOUT_REASONS}
        fields={
          <Field label="When did they leave?">
            <input
              className={uiCss.input}
              type="datetime-local"
              value={leftAt}
              min={checkingOut?.check_in ? toLocalInput(new Date(checkingOut.check_in.at_utc)) : undefined}
              max={toLocalInput(new Date())}
              onChange={(e) => setLeftAt(e.target.value)}
              aria-label="When they left"
            />
          </Field>
        }
        blocked={leftAtProblem(leftAt, checkingOut)}
        impact="The shift closes at this time. If it was the last shift, the job finishes and the family can pay and rate."
        okLabel="Check out"
        okTone="danger"
        onConfirm={({ code, note }) => {
          const a = checkingOut;
          setCheckingOut(null);
          if (!a) return;
          const label = CHECKOUT_REASONS.find((c) => c.code === code)?.label ?? code;
          const reason = (note.trim() ? `${label}: ${note.trim()}` : label).slice(0, 300);
          void run(
            () => checkOutOnBehalf(a.public_id, new Date(leftAt).toISOString(), reason),
            `${a.resource_label} was checked out at ${dateTime(new Date(leftAt).toISOString())}.`,
          ).then(refresh);
        }}
      />
    </>
  );
}

// Why an agency gives a job back (the server keeps the words on its offer).
const GIVE_BACK_REASONS = [
  { code: 'NO_AMBULANCE', label: 'No ambulance free' },
  { code: 'NO_DRIVER', label: 'No driver free' },
  { code: 'NO_STAFF', label: 'Nobody free for these dates' },
  { code: 'OTHER', label: 'Something else' },
];

// Why the agency checked someone out (the server stores the words, 1–300).
const CHECKOUT_REASONS = [
  { code: 'FORGOT', label: 'Forgot to check out' },
  { code: 'PHONE', label: 'Phone dead or no signal' },
  { code: 'LEFT_EARLY', label: 'Left before the shift ended' },
  { code: 'OTHER', label: 'Something else' },
];

/** Checked in (ACTIVE) on a shift whose end has passed. */
function stillCheckedIn(a: Assignment, shifts: Shift[]): boolean {
  if (a.status !== 'ACTIVE' || a.resource_type !== 'STAFF' || !a.shift_public_id) return false;
  const end = shifts.find((s) => s.public_id === a.shift_public_id)?.end_utc;
  return !!end && new Date(end).getTime() < Date.now();
}

/** A Date as the value of a datetime-local input (the browser's own clock). */
function toLocalInput(t: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${t.getFullYear()}-${p(t.getMonth() + 1)}-${p(t.getDate())}T${p(t.getHours())}:${p(t.getMinutes())}`;
}

function leftAtProblem(leftAt: string, a: Assignment | null): string | null {
  const t = new Date(leftAt).getTime();
  if (!leftAt || Number.isNaN(t)) return 'Choose when they left';
  if (t > Date.now()) return 'That time has not come yet';
  if (a?.check_in && t < new Date(a.check_in.at_utc).getTime()) return 'That is before they checked in';
  return null;
}

function liveOn(assignments: Assignment[], role: string, shiftId?: string): Assignment | undefined {
  return assignments.find((a) => LIVE.has(a.status) && a.role === role && (shiftId ? a.shift_public_id === shiftId : !a.shift_public_id));
}

function numberFrom(detail: Record<string, unknown> | null, service: string, key: string, fallback: number): number {
  const block = detail?.[service.toLowerCase()];
  if (block && typeof block === 'object') {
    const v = (block as Record<string, unknown>)[key];
    if (typeof v === 'number' && v > 0) return v;
  }
  const flat = detail?.[key];
  return typeof flat === 'number' && flat > 0 ? flat : fallback;
}

function AssigneeCell({ a, mayAct, onWithdraw }: { a: Assignment; mayAct: boolean; onWithdraw: (a: Assignment) => void }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <CellStack top={a.resource_label} sub={assignmentStatus(a.status)} />
      {mayAct && a.status !== 'ACTIVE' ? (
        <Btn size="xs" onClick={() => onWithdraw(a)}>
          Take off
        </Btn>
      ) : null}
    </span>
  );
}

function Staffing({
  item,
  mayAct,
  onPick,
  onWithdraw,
}: {
  item: ItemDetail;
  mayAct: boolean;
  onPick: (p: { slots: Slot[]; title: string }) => void;
  onWithdraw: (a: Assignment) => void;
}) {
  const svc = item.service_code;

  if (SHIFT_SERVICES.has(svc)) {
    const people = numberFrom(item.detail, svc, 'nurses_required', 1);
    const roles = Array.from({ length: people }, (_, i) => `NURSE_${i + 1}`);
    const upcoming = item.shifts.filter((s) => s.status !== 'CANCELLED' && new Date(s.end_utc).getTime() > Date.now());
    const open: Slot[] = [];
    for (const sh of upcoming) {
      for (const role of roles) {
        if (!liveOn(item.assignments, role, sh.public_id)) open.push({ role, shift: sh, label: `${dateOnly(sh.shift_date)} · ${sh.shift_type.toLowerCase()}` });
      }
    }
    // "One person for all": the open places of the first role that has any —
    // one person cannot be two of the carers on the same shift.
    const bulkRole = open[0]?.role;
    const bulk = open.filter((o) => o.role === bulkRole);
    const cols = `1.3fr ${roles.map(() => '1.2fr').join(' ')}`;
    return (
      <Card flat>
        <CardHead
          title={svc === 'DOCTOR' ? 'Doctor' : svc === 'PHYSIO' ? 'Visits' : 'Shifts'}
          sub={`${upcoming.length} still to come · ${open.length} place${open.length === 1 ? '' : 's'} with nobody`}
          right={
            mayAct && bulk.length > 1 ? (
              <Btn size="sm" variant="primary" onClick={() => onPick({ slots: bulk, title: `One ${roleName(bulkRole, svc).toLowerCase()} for every open shift` })}>
                One person for all open shifts
              </Btn>
            ) : null
          }
        />
        {upcoming.length === 0 ? (
          <Empty>No shifts ahead. {item.shifts.length ? 'Every shift has passed.' : 'The shifts appear once the job is set up — reopen it in a moment.'}</Empty>
        ) : (
          <Table cols={cols} head={['When', ...roles.map((r) => roleName(r, svc))]} minWidth={300 + roles.length * 200}>
            {upcoming.map((sh) => (
              <TRow key={sh.public_id} cols={cols}>
                <CellStack top={dateOnly(sh.shift_date)} sub={`${dateTime(sh.start_utc)} – ${dateTime(sh.end_utc)}`} />
                {roles.map((role) => {
                  const a = liveOn(item.assignments, role, sh.public_id);
                  return a ? (
                    <AssigneeCell key={role} a={a} mayAct={mayAct} onWithdraw={onWithdraw} />
                  ) : mayAct ? (
                    <span key={role}>
                      <Btn size="sm" onClick={() => onPick({ slots: [{ role, shift: sh, label: dateOnly(sh.shift_date) }], title: `Who goes on ${dateOnly(sh.shift_date)}?` })}>
                        Assign
                      </Btn>
                    </span>
                  ) : (
                    <span key={role} className={uiCss.sub}>
                      Nobody yet
                    </span>
                  );
                })}
              </TRow>
            ))}
          </Table>
        )}
      </Card>
    );
  }

  if (svc === 'AMBULANCE') {
    const amb = (item.detail?.ambulance ?? item.detail ?? {}) as Record<string, unknown>;
    const type = String(amb.ambulance_type ?? '');
    const needParamedic = type === 'ALS' || type === 'ICU';
    const attendants = typeof amb.attendant_count === 'number' ? amb.attendant_count : 0;
    const roles = ['VEHICLE', 'DRIVER', ...(needParamedic ? ['PARAMEDIC'] : [])];
    const crew = roles.map((r) => liveOn(item.assignments, r));
    const missing = roles.filter((_, i) => !crew[i]);
    const slots: Slot[] = [
      ...roles.map((role) => ({ role, label: roleName(role) })),
      ...Array.from({ length: attendants }, () => ({ role: 'ATTENDANT', label: 'Attendant' })),
    ];
    return (
      <Card flat>
        <CardHead
          title="The trip"
          sub={needParamedic ? `${type} ambulance: an ambulance, a driver and a paramedic.` : 'An ambulance and a driver.'}
          right={
            mayAct && missing.length === roles.length ? (
              <Btn size="sm" variant="primary" onClick={() => onPick({ slots, title: 'Choose the ambulance and crew' })}>
                Choose ambulance and crew
              </Btn>
            ) : null
          }
        />
        <div style={{ padding: '0 16px 16px', display: 'grid', gap: 10 }}>
          <Facts
            items={roles.map((r, i) => [roleName(r), crew[i] ? `${crew[i]!.resource_label} · ${assignmentStatus(crew[i]!.status)}` : 'Nobody yet'])}
          />
          {missing.length > 0 && missing.length < roles.length ? (
            <Note tone="amber">Part of the crew was taken off. Take off the rest under “Everyone on this job”, then choose the whole crew again.</Note>
          ) : null}
        </div>
      </Card>
    );
  }

  if (svc === 'EQUIPMENT') {
    const units = numberFrom(item.detail, svc, 'units_required', 1);
    const have = item.assignments.filter((a) => LIVE.has(a.status) && a.role === 'EQUIPMENT_UNIT').length;
    const left = Math.max(0, units - have);
    return (
      <Card flat>
        <CardHead
          title="Equipment"
          sub={`${have} of ${units} unit${units === 1 ? '' : 's'} assigned`}
          right={
            mayAct && left > 0 ? (
              <Btn
                size="sm"
                variant="primary"
                onClick={() => onPick({ slots: Array.from({ length: left }, () => ({ role: 'EQUIPMENT_UNIT', label: 'Unit' })), title: 'Choose the equipment units' })}
              >
                Choose units
              </Btn>
            ) : null
          }
        />
        {left === 0 ? <Empty>All units are assigned.</Empty> : null}
      </Card>
    );
  }

  return (
    <Card flat>
      <CardHead title="Staffing" />
      <Empty>This kind of job cannot be staffed from here.</Empty>
    </Card>
  );
}

const ACOLS = '1.4fr 0.9fr 0.9fr 1.3fr 1.3fr 0.7fr';

function AssignmentTable({
  assignments,
  shifts,
  mayAct,
  onWithdraw,
  onCheckOut,
  serviceCode,
}: {
  assignments: Assignment[];
  shifts: Shift[];
  mayAct: boolean;
  onWithdraw: (a: Assignment) => void;
  onCheckOut: (a: Assignment) => void;
  serviceCode?: string;
}) {
  const shiftDate = useMemo(() => new Map(shifts.map((s) => [s.public_id, s.shift_date])), [shifts]);
  if (assignments.length === 0) return <Empty>Nobody has been sent yet.</Empty>;
  return (
    <Table cols={ACOLS} head={['Who', 'As', 'Shift', 'Status', 'Check-in / out', '']} minWidth={760}>
      {assignments.map((a) => (
        <TRow key={a.public_id} cols={ACOLS}>
          <span style={{ font: '700 13px var(--font-ui)', color: 'var(--ink)' }}>{a.resource_label}</span>
          <span>{roleName(a.role, serviceCode)}</span>
          <span>{a.shift_public_id ? dateOnly(shiftDate.get(a.shift_public_id) ?? null) : 'Whole job'}</span>
          <CellStack
            top={assignmentStatus(a.status)}
            sub={[a.reject_reason ? `“${a.reject_reason}”` : '', a.unmet_criteria.length ? a.unmet_criteria.map(unmetText).join('; ') : '']
              .filter(Boolean)
              .join(' · ') || undefined}
          />
          <CellStack top={a.check_in ? dateTime(a.check_in.at_utc) : '—'} sub={a.check_out ? `out ${dateTime(a.check_out.at_utc)}` : undefined} />
          <span>
            {mayAct && (a.status === 'PROPOSED' || a.status === 'ACCEPTED') ? (
              <Btn size="xs" onClick={() => onWithdraw(a)}>
                Take off
              </Btn>
            ) : null}
            {mayAct && stillCheckedIn(a, shifts) ? (
              <Btn size="xs" variant="danger" onClick={() => onCheckOut(a)}>
                Check out
              </Btn>
            ) : null}
          </span>
        </TRow>
      ))}
    </Table>
  );
}

/* ------------------------------------------------------------------ picking */

/** Picks one resource per slot. For "one person for all open shifts" the
 *  candidates are read for the FIRST slot, and the chosen person is sent shift
 *  by shift: a bind is all-or-nothing, so one shift they are busy for must not
 *  sink the rest. What was refused is said, shift by shift. */
function PickDialog({
  item,
  slots,
  title,
  onClose,
  onBound,
}: {
  item: ItemDetail;
  slots: Slot[];
  title: string;
  onClose: () => void;
  onBound: (msg: string) => void;
}) {
  const samePerson = slots.length > 1 && slots.every((s) => s.shift && s.role === slots[0]!.role);
  // Distinct roles to pick for (ambulance bundle, equipment units), or one pick for all shifts.
  const picks: Slot[] = samePerson ? [slots[0]!] : slots;
  const [chosen, setChosen] = useState<(Candidate | null)[]>(() => picks.map(() => null));
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unmet = Array.from(new Set(chosen.flatMap((c) => c?.unmet ?? [])));
  const attendantSlots = picks.map((p, i) => (p.role === 'ATTENDANT' ? i : -1)).filter((i) => i >= 0);
  const required = picks.map((_, i) => i).filter((i) => !attendantSlots.includes(i));
  const blocked = busy
    ? 'Sending…'
    : !required.every((i) => chosen[i])
      ? 'Choose someone for every place first.'
      : unmet.length > 0 && !ack
        ? 'Tick “send anyway” to send someone who does not match what the family asked for.'
        : null;

  const bind = async () => {
    setBusy(true);
    setError(null);
    try {
      if (samePerson) {
        const c = chosen[0]!;
        let sent = 0;
        const refused: string[] = [];
        for (const sl of slots) {
          try {
            await bindItem(item.public_id, [{ shift: sl.shift!.public_id, role: sl.role, resource_type: c.resource_type, resource: c.resource_public_id }], unmet);
            sent++;
          } catch (e) {
            refused.push(`${sl.label}: ${errorText(e)}`);
          }
        }
        const msg = `${c.label} was sent to ${sent} of ${slots.length} shifts.` + (refused.length ? ` Not sent — ${refused.join(' · ')}` : '');
        if (sent > 0) onBound(msg);
        else setError(msg);
        return;
      }
      const body: BindAssignment[] = picks
        .map((p, i) => ({ p, c: chosen[i] }))
        .filter((x) => x.c)
        .map(({ p, c }) => ({ ...(p.shift ? { shift: p.shift.public_id } : {}), role: p.role, resource_type: c!.resource_type, resource: c!.resource_public_id }));
      const names = Array.from(new Set(chosen.filter(Boolean).map((c) => c!.label))).join(', ');
      await bindItem(item.public_id, body, unmet);
      onBound(`${names} ${body.length > 1 ? 'were' : 'was'} sent. Staff confirm in their app.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ActionWithReason
      open
      onClose={onClose}
      kicker="Choose who goes"
      title={title}
      desc={
        samePerson
          ? `${slots.length} open shifts. The person you choose is sent to every one they are free for.`
          : 'Eligible people first. Anyone the platform would refuse says why.'
      }
      codes={[]}
      fields={
        <div style={{ display: 'grid', gap: 14 }}>
          {error ? <Note tone="danger">{error}</Note> : null}
          {picks.map((p, i) => (
            <CandidateList
              key={`${p.role}-${i}`}
              item={item}
              slot={p}
              optional={p.role === 'ATTENDANT'}
              chosen={chosen[i] ?? null}
              taken={chosen.filter((c, j) => j !== i && c).map((c) => c!.resource_public_id)}
              onChoose={(c) => setChosen((prev) => prev.map((x, j) => (j === i ? c : x)))}
            />
          ))}
          {unmet.length ? (
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', font: '600 13px var(--font-ui)', color: 'var(--amber)' }}>
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} style={{ marginTop: 2 }} />
              Send anyway — {unmet.map(unmetText).join('; ').toLowerCase()}
            </label>
          ) : null}
        </div>
      }
      blocked={blocked}
      okLabel={busy ? 'Sending…' : 'Send'}
      okTone="primary"
      onConfirm={() => void bind()}
    />
  );
}

/** "+919828200002" as "+91 98282 00002". */
function phoneWords(p: string | null | undefined): string {
  if (!p) return '';
  const m = /^\+91(\d{5})(\d{5})$/.exec(p);
  return m ? `+91 ${m[1]} ${m[2]}` : p;
}

/** One line under a candidate's name: what the person is, their best
 *  qualification, whether they are on duty and their phone; for an ambulance,
 *  its type, the kit on board and the make. Empty on an older server. */
function detailLine(c: Candidate): string {
  if (c.staff) {
    const s = c.staff;
    // A CLINICAL person is a nurse or a physiotherapist: their qualification
    // says which, so it stands in for the kind when there is one.
    const kind = s.staff_type === 'CLINICAL' && s.qualification ? [s.qualification] : [staffType(s.staff_type), s.qualification ?? ''];
    return [...kind, s.on_duty ? 'On duty' : 'Off duty', phoneWords(s.phone)].filter(Boolean).join(' · ');
  }
  if (c.vehicle) {
    const v = c.vehicle;
    return [
      v.has_oxygen ? 'Oxygen' : 'No oxygen',
      v.has_ventilator ? 'Ventilator' : '',
      [v.make, v.model].filter(Boolean).join(' '),
    ]
      .filter(Boolean)
      .join(' · ');
  }
  return '';
}

function CandidateList({
  item,
  slot,
  optional,
  chosen,
  taken,
  onChoose,
}: {
  item: ItemDetail;
  slot: Slot;
  optional: boolean;
  chosen: Candidate | null;
  taken: string[];
  onChoose: (c: Candidate | null) => void;
}) {
  const [cands, setCands] = useState<Candidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    listCandidates(item.public_id, slot.role, slot.shift?.public_id)
      .then((c) => alive && setCands(c))
      .catch((e: unknown) => alive && setError(errorText(e)));
    return () => {
      alive = false;
    };
  }, [item.public_id, slot.role, slot.shift?.public_id]);

  const sorted = (cands ?? []).slice().sort((a, b) => Number(b.eligible) - Number(a.eligible) || Number(b.preferred) - Number(a.preferred));
  const name = `${slot.role}-${slot.label}`;
  const row = (on: boolean, off: boolean) => ({
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    padding: '9px 12px',
    border: `1px solid ${on ? 'var(--teal)' : 'var(--line-soft)'}`,
    background: on ? 'var(--teal-bg)' : 'var(--surface)',
    borderRadius: 10,
    opacity: off ? 0.6 : 1,
    cursor: off ? 'not-allowed' : 'pointer',
  });
  return (
    <div>
      <div className={uiCss.label} style={{ marginBottom: 7 }}>
        {roleName(slot.role, item.service_code)}
        {slot.shift ? ` · ${slot.label}` : ''}
        {optional ? ' (optional)' : ''}
      </div>
      {error ? <Note tone="danger">{error}</Note> : null}
      {!cands && !error ? <div className={uiCss.sub}>Reading your team…</div> : null}
      {cands && cands.length === 0 ? (
        <div className={uiCss.sub}>
          {slot.role === 'VEHICLE'
            ? 'No ambulance on your team. Add one under Team.'
            : slot.role === 'DRIVER'
              ? 'No driver on your team. A driver who joins you from the partner app appears here once you confirm them under Team.'
              : `Nobody on your team can go as ${roleName(slot.role, item.service_code).toLowerCase()}. Staff who join you appear under Team.`}
        </div>
      ) : null}
      <div style={{ display: 'grid', gap: 6 }}>
        {optional && cands && cands.length ? (
          <label style={row(chosen === null, false)}>
            <input type="radio" name={name} checked={chosen === null} onChange={() => onChoose(null)} />
            <span style={{ font: '600 13px var(--font-ui)' }}>Nobody</span>
          </label>
        ) : null}
        {sorted.map((c) => {
          const off = !c.eligible || taken.includes(c.resource_public_id);
          const on = chosen?.resource_public_id === c.resource_public_id;
          return (
            <label key={c.resource_public_id} style={row(on, off)}>
              <input type="radio" name={name} disabled={off} checked={on} onChange={() => onChoose(c)} style={{ marginTop: 2 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', font: '700 13px var(--font-ui)', color: 'var(--ink)' }}>
                  {c.label}
                  {c.preferred ? (
                    <Tag fg="var(--teal)" bg="var(--teal-bg)">
                      Family asked for them
                    </Tag>
                  ) : null}
                  {c.eligible ? null : (
                    <Tag fg="var(--danger)" bg="var(--danger-bg)">
                      Cannot go
                    </Tag>
                  )}
                </span>
                {detailLine(c) ? (
                  <span className={uiCss.sub} style={{ display: 'block', color: 'var(--ink-soft)' }}>
                    {detailLine(c)}
                  </span>
                ) : null}
                {c.refusals.length ? (
                  <span className={uiCss.sub} style={{ display: 'block', color: 'var(--danger)' }}>
                    {Array.from(new Set(c.refusals.map((r) => refusalText(r.code, r.reason)))).join(' ')}
                  </span>
                ) : null}
                {c.eligible && c.unmet.length ? (
                  <span className={uiCss.sub} style={{ display: 'block', color: 'var(--amber)' }}>
                    {c.unmet.map(unmetText).join('; ')}
                  </span>
                ) : null}
                {taken.includes(c.resource_public_id) ? <span className={uiCss.sub} style={{ display: 'block' }}>Already chosen above.</span> : null}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
